import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import capitalize from 'lodash/capitalize';

import {
  ContentType as ContentTypeGraphQL,
  LanguageIntensity as LanguageIntensityGraphQL,
  ParticipantsType,
  ParticipantType,
  StoryDetailsForSearchFragment,
  StoryTileFragment,
  ThreeSixtyStory,
} from 'graphql/generated/graphql';

import { IntlMsgShape } from 'helpers/consts';

import { SearchSortBy } from 'hooks/search/types';

import { applyFilters } from 'store/modules/algolia_filters/apply_filters';

import { ContentType } from 'types/contentType';
import { LanguageIntensity } from 'types/languageIntensity';

export type SortingOption = 'relevance' | 'kinkiness' | 'new' | 'popular';
export type DurationRange =
  | 'less_than_five'
  | 'five_to_ten'
  | 'ten_to_fifteen'
  | 'more_than_fifteen';
export type SearchParticipantType = ParticipantType | 'couple';

export interface IAlgoliaTags {
  id: string;
  name: string;
}

export type AlgoliaStory = {
  objectID: string;
  title: string;
  content_type: Pick<ContentTypeGraphQL, 'slug'>;
  duration: number;
  language_intensity: Pick<LanguageIntensityGraphQL, 'slug'> & { id: number };
  markets: Array<string>;
  speaker: {
    id: number;
    name: string;
    participants_type: Pick<ParticipantsType, 'slug'>;
  };
  theme_tag: {
    id: number;
    name_de: string;
    name_en_gb: string;
    name_fr_fr: string;
  };
  description: string;
  duration_in_minutes: string;
  duration_range: DurationRange;
  femtasy_red: boolean;
  kinkiness_score: number;
  last_week_listens_de: number;
  last_week_listens_en_gb: number;
  last_week_listens_en_us: number;
  last_week_listens_fr_fr: number;
  release_date_timestamp_seconds: number;
  transcoded_audio_url: string;
  genre: {
    id: number;
    name_de: string;
    name_en_gb: string;
    name_fr_fr: string;
  };
  tags: Array<{
    id: number;
    name_de: string;
    name_en_gb: string;
    name_fr_fr: string;
  }>;
};

export interface StaticFilter {
  text: IntlMsgShape | string;
  filter: { type: FilterType; value: any };
}
export enum StoryIntro {
  WithIntro = 'with-intro',
  NoIntro = 'no-intro',
  OnlyMoaning = 'only-moaning',
}

export type AvailableFilters = {
  contentTypes: Array<ContentType>;
  durationRanges: Array<DurationRange>;
  languageIntensities: Array<LanguageIntensity>;
  participantsTypes: Array<SearchParticipantType>;
  scenarios: Array<string>;
  themeTags: Array<string>;
  tagIds: Array<string>;
  intros: Array<StoryIntro>;
};

export type SelectedFilters = AvailableFilters & {
  noGoes: Array<string>;
  sorting: SearchSortBy;
};

export type FacetKeys =
  | 'tags.id'
  | 'content_type.slug'
  | 'duration_in_range_v2'
  | 'language_intensity.slug'
  | 'speaker.participants_type.slug'
  | 'theme_tag.id'
  | 'scenario_tag.id'
  | 'intro';

export type SearchResult = {
  page: number;
  nbHits: number;
  nbPages: number;
  hitsPerPage: number;
  hits: Array<AlgoliaStory>;
  facets?: Record<FacetKeys, Record<string, number>>;
};

export type Results = {
  searchResults: SearchResult;
  stories: Array<StoryTileFragment & { tags: Array<IAlgoliaTags> }>;
  totalCount: number;
};

export enum Screen {
  FilterScreen,
  SearchResultsScreen,
  SpeakerStories,
}

type FilterScreen = {
  availableFilters: AvailableFilters;
  resultCount: number;
  results: Results;
  selectedFilters: SelectedFilters;
  excludeAlreadyListenedStories: boolean | undefined;
};

export enum FilterType {
  ContentType = 'contentType',
  DurationRange = 'durationRange',
  LanguageIntensity = 'languageIntensity',
  ParticipantsType = 'participantsType',
  ThemeTag = 'themeTag',
  Sorting = 'sorting',
  NoGo = 'no-go',
  Scenario = 'scenario',
  ExcludeAlreadyListenedStories = 'excludeAlreadyListenedStories',
  Tags = 'tags',
  Intro = 'intro',
}

export type SearchParams =
  | {
      title?: string;
      tagId?: string;
      speakerId?: never;
      genreName?: never;
      contentTypeName?: never;
      languageIntensityName?: never;
      participantsTypes?: never;
    }
  | {
      title?: string;
      tagId?: never;
      speakerId?: string;
      genreName?: never;
      contentTypeName?: never;
      languageIntensityName?: never;
      participantsTypes?: never;
    }
  | {
      title?: string;
      tagId?: never;
      speakerId?: never;
      genreName?: string;
      contentTypeName?: never;
      languageIntensityName?: never;
      participantsTypes?: never;
    }
  | {
      title?: string;
      tagId?: never;
      speakerId?: never;
      genreName?: never;
      contentTypeName?: string;
      languageIntensityName?: never;
      participantsTypes?: never;
    }
  | {
      title?: string;
      tagId?: never;
      speakerId?: never;
      genreName?: never;
      contentTypeName?: never;
      languageIntensityName?: string;
      participantsTypes?: never;
    }
  | {
      title?: string;
      tagId?: never;
      speakerId?: never;
      genreName?: never;
      contentTypeName?: never;
      languageIntensityName?: never;
      participantsTypes?: ParticipantType[];
    };

export type AlgoliaFiltersState = {
  errored: boolean;
  filterScreenChanged: boolean;
  filterScreenShown: boolean;
  loading: boolean;
  loaded: boolean;
  searchParams?: SearchParams;
  filterScreen: FilterScreen;
  searchScreen: FilterScreen;
};

const availableFiltersInitialState: AvailableFilters = {
  contentTypes: [],
  durationRanges: [],
  languageIntensities: [],
  participantsTypes: [],
  scenarios: [],
  themeTags: [],
  tagIds: [],
  intros: [],
};

const selectedFiltersInitialState: SelectedFilters = {
  ...availableFiltersInitialState,
  noGoes: [],
  sorting: SearchSortBy.Relevance,
};

const screenInitialState: FilterScreen = {
  availableFilters: availableFiltersInitialState,
  resultCount: 0,
  results: {
    searchResults: {
      hits: [],
      hitsPerPage: 0,
      nbHits: 0,
      nbPages: 0,
      page: -1,
    },
    stories: [],
    totalCount: 0,
  },
  selectedFilters: selectedFiltersInitialState,
  excludeAlreadyListenedStories: undefined,
};

export const initialAlgoliaFiltersState: AlgoliaFiltersState = {
  errored: false,
  filterScreenChanged: false,
  filterScreenShown: false,
  loading: false,
  loaded: false,
  filterScreen: screenInitialState,
  searchScreen: screenInitialState,
};

const couplesParticipantTypes = [
  ParticipantType.FemaleFemale,
  ParticipantType.FemaleMale,
  ParticipantType.MaleFemale,
  ParticipantType.MaleMale,
  ParticipantType.Anonym,
  'couple',
];

export const getContentTypes = (searchResults: SearchResult) => {
  if (
    !searchResults ||
    !searchResults.facets ||
    !searchResults.facets['content_type.slug']
  )
    return [];
  else {
    return Object.keys(searchResults.facets['content_type.slug']).map(
      (key: string): ContentType => {
        return ContentType[capitalize(key)];
      },
    );
  }
};

export const getDurationRanges = (searchResults: SearchResult) => {
  if (
    !searchResults ||
    !searchResults.facets ||
    !searchResults.facets['duration_in_range_v2']
  )
    return [];
  else {
    return Object.keys(
      searchResults.facets['duration_in_range_v2'],
    ) as Array<DurationRange>;
  }
};

export const getLanguageIntensities = (searchResults: SearchResult) => {
  if (
    !searchResults ||
    !searchResults.facets ||
    !searchResults.facets['language_intensity.slug']
  )
    return [];
  else {
    return Object.keys(searchResults.facets['language_intensity.slug']).map(
      (key: string): LanguageIntensity => {
        return LanguageIntensity[capitalize(key)];
      },
    );
  }
};

export const getParticipantsTypes = (searchResults: SearchResult) => {
  if (
    !searchResults ||
    !searchResults.facets ||
    !searchResults.facets['speaker.participants_type.slug']
  )
    return [];
  else {
    return Object.keys(
      searchResults.facets['speaker.participants_type.slug'],
    ) as Array<ParticipantType>;
  }
};

export const getThemeTags = (searchResults: SearchResult) => {
  if (
    !searchResults ||
    !searchResults.facets ||
    !searchResults.facets['theme_tag.id']
  )
    return [];
  else {
    return Object.keys(searchResults.facets['theme_tag.id']);
  }
};

export const getTagIds = (searchResults: SearchResult) => {
  if (
    !searchResults ||
    !searchResults.facets ||
    !searchResults.facets['tags.id']
  )
    return [];
  else {
    return Object.keys(searchResults.facets['tags.id']);
  }
};

export const getIntros = (searchResults: SearchResult) => {
  if (!searchResults || !searchResults.facets || !searchResults.facets['intro'])
    return [];
  else {
    return Object.keys(searchResults.facets['intro']) as Array<StoryIntro>;
  }
};

export const getScenariosFromSearchResults = (searchResults: SearchResult) => {
  if (
    !searchResults ||
    !searchResults.facets ||
    !searchResults.facets['scenario_tag.id']
  )
    return [];
  else {
    return Object.keys(searchResults.facets['scenario_tag.id']);
  }
};

export const transformAvailableFiltersFromFacets = (
  results: Results,
): AvailableFilters => {
  return {
    contentTypes: getContentTypes(results.searchResults),
    durationRanges: getDurationRanges(results.searchResults),
    languageIntensities: getLanguageIntensities(results.searchResults),
    participantsTypes: getParticipantsTypes(results.searchResults),
    scenarios: getScenariosFromSearchResults(results.searchResults),
    themeTags: getThemeTags(results.searchResults),
    tagIds: getTagIds(results.searchResults),
    intros: getIntros(results.searchResults),
  };
};

export const algoliaSlice = createSlice({
  name: 'algoliaFilters',
  initialState: initialAlgoliaFiltersState,
  extraReducers: (builder) => {
    builder.addCase(applyFilters.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(applyFilters.rejected, (state) => {
      state.loading = false;
    });
  },
  reducers: {
    // Called when the search results page is initially opened
    reset: (state) => ({
      ...initialAlgoliaFiltersState,
      searchScreen: {
        ...initialAlgoliaFiltersState.searchScreen,
        excludeAlreadyListenedStories:
          state.searchScreen.excludeAlreadyListenedStories,
      },
      filterScreen: {
        ...initialAlgoliaFiltersState.filterScreen,
        excludeAlreadyListenedStories:
          state.filterScreen.excludeAlreadyListenedStories,
      },
    }),
    setErrored: (state, action: PayloadAction<boolean>) => {
      state.errored = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      state.errored = false;
    },
    setLoaded: (state, action: PayloadAction<boolean>) => {
      state.loaded = action.payload;
    },
    // Show/hide the filters screen
    setFilterScreenShown: (state, action: PayloadAction<boolean>) => {
      state.filterScreenShown = action.payload;
    },
    setExcludeAlreadyListenedStories: (
      state,
      action: PayloadAction<{
        excludeAlreadyListenedStories: boolean;
        screen: Screen;
      }>,
    ) => {
      if (action.payload.screen === Screen.FilterScreen) {
        state.filterScreen.excludeAlreadyListenedStories =
          action.payload.excludeAlreadyListenedStories;

        state.filterScreen.results =
          initialAlgoliaFiltersState.filterScreen.results;
        state.filterScreenChanged = true;
      } else {
        state.searchScreen.excludeAlreadyListenedStories =
          action.payload.excludeAlreadyListenedStories;

        state.searchScreen.results =
          initialAlgoliaFiltersState.searchScreen.results;
      }
    },
    deleteExcludeAlreadyListenedStories: (state) => {
      state.filterScreen.excludeAlreadyListenedStories = false;
      state.searchScreen.excludeAlreadyListenedStories = false;

      state.searchScreen.results =
        initialAlgoliaFiltersState.searchScreen.results;
      state.filterScreen.results =
        initialAlgoliaFiltersState.filterScreen.results;
    },
    refreshResults: (state) => {
      state.searchScreen.results =
        initialAlgoliaFiltersState.searchScreen.results;
      state.filterScreen.results =
        initialAlgoliaFiltersState.filterScreen.results;
    },
    setSearchParams: (state, action: PayloadAction<SearchParams>) => {
      state.searchParams = action.payload;
    },
    // Called when we receive results on the search results page
    setAvailableFilters: (state, action: PayloadAction<AvailableFilters>) => {
      state.searchScreen.availableFilters = action.payload;
      state.filterScreen.availableFilters = action.payload;
    },
    appendSelectedFilters: (
      state,
      action: PayloadAction<Partial<SelectedFilters>>,
    ) => {
      state.searchScreen.selectedFilters = {
        ...state.searchScreen.selectedFilters,
        ...action.payload,
      };
      state.filterScreen.selectedFilters = {
        ...state.filterScreen.selectedFilters,
        ...action.payload,
      };
    },
    // Called when we receive results on the filters screen
    setAvailableFiltersFilterScreen: (
      state,
      action: PayloadAction<AvailableFilters>,
    ) => {
      state.filterScreen.availableFilters = action.payload;
    },
    // Called when we receive results on the search results page
    setResults: (state, action: PayloadAction<Results>) => {
      state.searchScreen.results = action.payload;
      state.searchScreen.availableFilters = transformAvailableFiltersFromFacets(
        action.payload,
      );

      state.filterScreen = state.searchScreen;
    },
    // Called when we receive results on the filters screen
    setResultsFilterScreen: (state, action: PayloadAction<Results>) => {
      state.filterScreen.results = action.payload;

      state.filterScreen.availableFilters = transformAvailableFiltersFromFacets(
        action.payload,
      );
    },
    // Called if the user cancels the filters screen
    cancelFiltering: (state) => {
      state.filterScreen = state.searchScreen;
    },
    // Called when the user clicks on the CTA on the filters screen
    commitFiltering: (state) => {
      state.searchScreen = state.filterScreen;
    },
    // Setters and deleters for each of the filters
    setContentTypesFilterScreen: (
      state,
      action: PayloadAction<Array<ContentType>>,
    ) => {
      state.filterScreen.selectedFilters.contentTypes = action.payload;
      state.filterScreenChanged = true;
    },
    deleteContentType: (state, action: PayloadAction<string>) => {
      state.searchScreen.selectedFilters.contentTypes =
        state.searchScreen.selectedFilters.contentTypes.filter(
          (el) => el !== action.payload,
        );
      state.filterScreen.selectedFilters.contentTypes =
        state.searchScreen.selectedFilters.contentTypes;

      state.searchScreen.results =
        initialAlgoliaFiltersState.searchScreen.results;
      state.filterScreen.results =
        initialAlgoliaFiltersState.filterScreen.results;
    },
    setIntrosFilterScreen: (
      state,
      action: PayloadAction<Array<StoryIntro>>,
    ) => {
      state.filterScreen.selectedFilters.intros = action.payload;
      state.filterScreenChanged = true;
    },
    setParticipantsTypesFilterScreen: (
      state,
      action: PayloadAction<Array<SearchParticipantType>>,
    ) => {
      state.filterScreen.selectedFilters.participantsTypes = action.payload;
      state.filterScreenChanged = true;
    },
    deleteParticipantsTypes: (state, action: PayloadAction<string>) => {
      state.searchScreen.selectedFilters.participantsTypes =
        state.searchScreen.selectedFilters.participantsTypes.filter((el) => {
          return (
            el !== action.payload &&
            !(
              action.payload === 'couple' &&
              couplesParticipantTypes.includes(el)
            )
          );
        });
      state.filterScreen.selectedFilters.participantsTypes =
        state.searchScreen.selectedFilters.participantsTypes;

      state.searchScreen.results =
        initialAlgoliaFiltersState.searchScreen.results;
      state.filterScreen.results =
        initialAlgoliaFiltersState.filterScreen.results;
    },
    setDurationRangesFilterScreen: (
      state,
      action: PayloadAction<Array<DurationRange>>,
    ) => {
      state.filterScreen.selectedFilters.durationRanges = action.payload;
      state.filterScreenChanged = true;
    },
    deleteDurationRanges: (state, action: PayloadAction<DurationRange>) => {
      state.searchScreen.selectedFilters.durationRanges =
        state.searchScreen.selectedFilters.durationRanges.filter(
          (el) => el !== action.payload,
        );
      state.filterScreen.selectedFilters.durationRanges =
        state.searchScreen.selectedFilters.durationRanges;

      state.searchScreen.results =
        initialAlgoliaFiltersState.searchScreen.results;
      state.filterScreen.results =
        initialAlgoliaFiltersState.filterScreen.results;
    },
    setLanguageIntensitiesFilterScreen: (
      state,
      action: PayloadAction<Array<LanguageIntensity>>,
    ) => {
      state.filterScreen.selectedFilters.languageIntensities = action.payload;
      state.filterScreenChanged = true;
    },
    deleteLanguageIntensities: (state, action: PayloadAction<string>) => {
      state.searchScreen.selectedFilters.languageIntensities =
        state.searchScreen.selectedFilters.languageIntensities.filter(
          (el) => el !== action.payload,
        );
      state.filterScreen.selectedFilters.languageIntensities =
        state.searchScreen.selectedFilters.languageIntensities;

      state.searchScreen.results =
        initialAlgoliaFiltersState.searchScreen.results;
      state.filterScreen.results =
        initialAlgoliaFiltersState.filterScreen.results;
    },
    setThemeTagsFilterScreen: (state, action: PayloadAction<Array<string>>) => {
      state.filterScreen.selectedFilters.themeTags = action.payload;
      state.filterScreenChanged = true;
    },
    deleteThemeTags: (state, action: PayloadAction<string>) => {
      state.searchScreen.selectedFilters.themeTags =
        state.searchScreen.selectedFilters.themeTags.filter(
          (el) => el !== action.payload,
        );
      state.filterScreen.selectedFilters.themeTags =
        state.searchScreen.selectedFilters.themeTags;

      state.searchScreen.results =
        initialAlgoliaFiltersState.searchScreen.results;
      state.filterScreen.results =
        initialAlgoliaFiltersState.filterScreen.results;
    },
    setScenariosFilterScreen: (state, action: PayloadAction<Array<string>>) => {
      state.filterScreen.selectedFilters.scenarios = action.payload;
      state.filterScreenChanged = true;
    },
    deleteScenarios: (state, action: PayloadAction<string>) => {
      state.searchScreen.selectedFilters.scenarios =
        state.searchScreen.selectedFilters.scenarios.filter(
          (el) => el !== action.payload,
        );
      state.filterScreen.selectedFilters.scenarios =
        state.searchScreen.selectedFilters.scenarios;

      state.searchScreen.results =
        initialAlgoliaFiltersState.searchScreen.results;
      state.filterScreen.results =
        initialAlgoliaFiltersState.filterScreen.results;
    },
    setNoGoesFilterScreen: (state, action: PayloadAction<Array<string>>) => {
      state.filterScreen.selectedFilters.noGoes = action.payload;
      state.filterScreenChanged = true;
    },
    deleteNoGoes: (state, action: PayloadAction<string>) => {
      state.searchScreen.selectedFilters.noGoes =
        state.searchScreen.selectedFilters.noGoes.filter(
          (el) => el !== action.payload,
        );
      state.filterScreen.selectedFilters.noGoes =
        state.searchScreen.selectedFilters.noGoes;

      state.searchScreen.results =
        initialAlgoliaFiltersState.searchScreen.results;
      state.filterScreen.results =
        initialAlgoliaFiltersState.filterScreen.results;
    },
    setSortingFilterScreen: (state, action: PayloadAction<SearchSortBy>) => {
      state.filterScreen.selectedFilters.sorting = action.payload;
      state.filterScreenChanged = true;
    },
    resetSorting: (state) => {
      state.filterScreen.selectedFilters.sorting = SearchSortBy.Relevance;
      state.searchScreen.selectedFilters.sorting = SearchSortBy.Relevance;

      state.searchScreen.results =
        initialAlgoliaFiltersState.searchScreen.results;
      state.filterScreen.results =
        initialAlgoliaFiltersState.filterScreen.results;
    },
    setTagIdsSearchScreen: (state, action: PayloadAction<Array<string>>) => {
      state.filterScreen.selectedFilters.tagIds = action.payload;
      state.filterScreenChanged = true;
    },
    updateUserDependantInfoOnStory: (
      state,
      action: PayloadAction<
        StoryDetailsForSearchFragment | Partial<ThreeSixtyStory>
      >,
    ) => {
      const updateStory = (story) => {
        if (story.id === action.payload.id) {
          return {
            ...story,
            loved: !!action.payload.loved,
            playlists: action.payload.playlists,
            alreadyListened: !!action.payload.alreadyListened,
          };
        }
        return story;
      };

      state.searchScreen.results.stories =
        state.searchScreen.results.stories.map(updateStory);
      state.filterScreen.results.stories =
        state.filterScreen.results.stories.map(updateStory);
    },
  },
});

export const {
  cancelFiltering,
  commitFiltering,
  deleteExcludeAlreadyListenedStories,
  deleteContentType,
  deleteDurationRanges,
  deleteLanguageIntensities,
  deleteNoGoes,
  deleteParticipantsTypes,
  deleteScenarios,
  deleteThemeTags,
  reset,
  resetSorting,
  setAvailableFilters,
  setAvailableFiltersFilterScreen,
  setContentTypesFilterScreen,
  setIntrosFilterScreen,
  setDurationRangesFilterScreen,
  setFilterScreenShown,
  setExcludeAlreadyListenedStories,
  setErrored,
  setLanguageIntensitiesFilterScreen,
  setLoading,
  setLoaded,
  setNoGoesFilterScreen,
  setParticipantsTypesFilterScreen,
  setResults,
  setResultsFilterScreen,
  setScenariosFilterScreen,
  setSearchParams,
  setTagIdsSearchScreen,
  appendSelectedFilters,
  setSortingFilterScreen,
  setThemeTagsFilterScreen,
  updateUserDependantInfoOnStory,
  refreshResults,
} = algoliaSlice.actions;

type SelectorState = { algolia_filters: AlgoliaFiltersState };

export const getErrored = (state: SelectorState) =>
  state.algolia_filters.errored;

export const getLoading = (state: SelectorState) =>
  state.algolia_filters.loading;

export const getLoaded = (state: SelectorState) => state.algolia_filters.loaded;

export const getFilterScreenChanged = (state: SelectorState) =>
  state.algolia_filters.filterScreenChanged;

export const getFilterScreenAvailableFilters = (state: SelectorState) =>
  state.algolia_filters.filterScreen.availableFilters;

export const getFilterScreenSelectedFilters = (state: SelectorState) =>
  state.algolia_filters.filterScreen.selectedFilters;

export const getFilterScreenResultCount = (state: SelectorState) =>
  state.algolia_filters.filterScreen.results.searchResults.nbHits;

export const getFilterScreenSelectedContentTypes = (state: SelectorState) =>
  state.algolia_filters.filterScreen.selectedFilters.contentTypes;
export const getFilterScreenSelectedDurationRanges = (state: SelectorState) =>
  state.algolia_filters.filterScreen.selectedFilters.durationRanges;
export const getFilterScreenSelectedLanguageIntensities = (
  state: SelectorState,
) => state.algolia_filters.filterScreen.selectedFilters.languageIntensities;
export const getFilterScreenSelectedNoGoes = (state: SelectorState) =>
  state.algolia_filters.filterScreen.selectedFilters.noGoes;
export const getFilterScreenSelectedParticipantsTypes = (
  state: SelectorState,
) => state.algolia_filters.filterScreen.selectedFilters.participantsTypes;
export const getFilterScreenSelectedScenarios = (state: SelectorState) =>
  state.algolia_filters.filterScreen.selectedFilters.scenarios;
export const getFilterScreenSelectedSorting = (state: SelectorState) =>
  state.algolia_filters.filterScreen.selectedFilters.sorting;
export const getFilterScreenSelectedThemeTags = (state: SelectorState) =>
  state.algolia_filters.filterScreen.selectedFilters.themeTags;

export const getFilterScreenAvailableContentTypes = (state: SelectorState) =>
  state.algolia_filters.filterScreen.availableFilters.contentTypes;
export const getFilterScreenAvailableDurationRanges = (state: SelectorState) =>
  state.algolia_filters.filterScreen.availableFilters.durationRanges;
export const getFilterScreenAvailableLanguageIntensities = (
  state: SelectorState,
) => state.algolia_filters.filterScreen.availableFilters.languageIntensities;
export const getFilterScreenAvailableParticipantsTypes = (
  state: SelectorState,
) => state.algolia_filters.filterScreen.availableFilters.participantsTypes;
export const getFilterScreenAvailableScenarios = (state: SelectorState) =>
  state.algolia_filters.filterScreen.availableFilters.scenarios;
export const getFilterScreenAvailableThemeTags = (state: SelectorState) =>
  state.algolia_filters.filterScreen.availableFilters.themeTags;

export const getSearchParams = (state: SelectorState) =>
  state.algolia_filters.searchParams;

export const getSearchScreen = (state: SelectorState) =>
  state.algolia_filters.searchScreen;

export const getExcludeAlreadyListenedStories =
  (screen: Screen) => (state: SelectorState) => {
    if (screen === Screen.FilterScreen) {
      return state.algolia_filters.filterScreen.excludeAlreadyListenedStories;
    } else {
      return state.algolia_filters.searchScreen.excludeAlreadyListenedStories;
    }
  };

export const getSearchScreenAvailableContentTypes = (state: SelectorState) =>
  state.algolia_filters.searchScreen.availableFilters.contentTypes;

export const getSearchScreenSelectedContentTypes = (state: SelectorState) =>
  state.algolia_filters.searchScreen.selectedFilters.contentTypes;

export const getSearchScreenSelectedIntros = (state: SelectorState) =>
  state.algolia_filters.searchScreen.selectedFilters.intros;

export const getSearchScreenAvailableIntros = (state: SelectorState) =>
  state.algolia_filters.searchScreen.availableFilters.intros;

export const getSearchScreenAvailableTagIds = (state: SelectorState) =>
  state.algolia_filters.searchScreen.availableFilters.tagIds;

export const getSearchScreenSelectedTagIds = (state: SelectorState) =>
  state.algolia_filters.searchScreen.selectedFilters.tagIds;

const compactParticipantsTypes = (
  participantsTypes: SearchParticipantType[],
) => {
  if (
    participantsTypes.find((participanType) =>
      couplesParticipantTypes.includes(participanType),
    )
  ) {
    const filteredParticipantTypes = participantsTypes.filter(
      (participanType) => !couplesParticipantTypes.includes(participanType),
    );

    filteredParticipantTypes.push('couple');

    return filteredParticipantTypes;
  }

  return participantsTypes;
};

export const getSearchScreenSelectedFilters = (state: SelectorState) =>
  state.algolia_filters.searchScreen.selectedFilters;

export const getSearchScreenSelectedFiltersAsArray = (state: SelectorState) => {
  const selectedFilters = state.algolia_filters.searchScreen.selectedFilters;

  return [
    ...(selectedFilters.sorting === SearchSortBy.Relevance
      ? []
      : [
          {
            type: FilterType.Sorting,
            value: selectedFilters.sorting,
          },
        ]),
    ...selectedFilters.contentTypes.map((contentType) => ({
      type: FilterType.ContentType,
      value: contentType,
    })),
    ...selectedFilters.durationRanges.map((durationRange) => ({
      type: FilterType.DurationRange,
      value: durationRange,
    })),
    ...selectedFilters.languageIntensities.map((languageIntensity) => ({
      type: FilterType.LanguageIntensity,
      value: languageIntensity,
    })),
    ...compactParticipantsTypes(selectedFilters.participantsTypes).map(
      (participantsType) => ({
        type: FilterType.ParticipantsType,
        value: participantsType,
      }),
    ),
    ...selectedFilters.themeTags.map((themeTag) => ({
      type: FilterType.ThemeTag,
      value: themeTag,
    })),
    ...selectedFilters.noGoes.map((nogo) => ({
      type: FilterType.NoGo,
      value: nogo,
    })),
    ...selectedFilters.scenarios.map((scenario) => ({
      type: FilterType.Scenario,
      value: scenario,
    })),
    ...(state.algolia_filters.searchScreen.excludeAlreadyListenedStories
      ? [
          {
            type: FilterType.ExcludeAlreadyListenedStories,
            value:
              state.algolia_filters.searchScreen.excludeAlreadyListenedStories,
          },
        ]
      : []),
  ];
};

export const getResults = (state: SelectorState) =>
  state.algolia_filters.searchScreen.results;

export const isFilterScreenShown = (state: SelectorState) =>
  state.algolia_filters.filterScreenShown;

export default algoliaSlice.reducer;
